import { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, CheckCircle, Wallet, Sparkles, ExternalLink } from 'lucide-react';
import { useTelegram } from '../../hooks';

interface WithdrawalCompletedData {
  withdrawalId: string;
  amount: number;
  paymentMethod: string;
  transactionRef: string | null;
}

interface WithdrawalCompletedPopupProps {
  data: WithdrawalCompletedData | null;
  onClose: () => void;
}

export function WithdrawalCompletedPopup({ data, onClose }: WithdrawalCompletedPopupProps) {
  const [isVisible, setIsVisible] = useState(false);
  const { hapticNotification } = useTelegram();

  useEffect(() => {
    if (data) {
      setIsVisible(true);
      // Success haptic feedback
      hapticNotification('success');
    }
  }, [data, hapticNotification]);

  const handleClose = useCallback(() => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  }, [onClose]);

  if (!data) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/80 backdrop-blur-md"
          onClick={(e) => e.target === e.currentTarget && handleClose()}
        >
          <motion.div
            initial={{ scale: 0.5, opacity: 0, y: 50 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.5, opacity: 0, y: 50 }}
            transition={{ type: 'spring', damping: 20, stiffness: 300 }}
            className="relative w-full max-w-sm overflow-hidden"
          >
            {/* Main Card */}
            <div className="relative rounded-3xl bg-gradient-to-br from-blue-900/90 via-indigo-800/90 to-purple-900/90 border border-blue-500/30 shadow-2xl">
              
              {/* Close Button */}
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 transition-colors z-10"
              >
                <X className="w-5 h-5 text-white/80" />
              </button>

              {/* Icon Animation */}
              <div className="flex justify-center pt-8 pb-4">
                <motion.div
                  initial={{ scale: 0, rotate: -180 }}
                  animate={{ scale: 1, rotate: 0 }}
                  transition={{ type: 'spring', delay: 0.2, damping: 10 }}
                  className="relative p-6 rounded-full bg-gradient-to-br from-green-400 to-emerald-500 shadow-lg shadow-green-500/50"
                >
                  <CheckCircle className="w-10 h-10 text-white" />
                  
                  {/* Sparkle Effects */}
                  <motion.div
                    animate={{ 
                      scale: [1, 1.3, 1],
                      opacity: [0.5, 1, 0.5],
                    }}
                    transition={{ duration: 2, repeat: Infinity }}
                    className="absolute -top-2 -right-2"
                  >
                    <Sparkles className="w-6 h-6 text-yellow-300" />
                  </motion.div>
                  <motion.div
                    animate={{ 
                      scale: [1, 1.2, 1],
                      opacity: [0.3, 0.8, 0.3],
                    }}
                    transition={{ duration: 1.5, repeat: Infinity, delay: 0.5 }}
                    className="absolute -bottom-1 -left-2"
                  >
                    <Sparkles className="w-4 h-4 text-yellow-200" />
                  </motion.div>
                </motion.div>
              </div>

              {/* Content */}
              <div className="text-center px-6 pb-6">
                <motion.h2
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="text-2xl font-bold text-white mb-2"
                >
                  🎉 Withdrawal Completed!
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.35 }}
                  className="text-white/70 text-sm mb-4"
                >
                  Your payment has been sent successfully
                </motion.p>

                {/* Amount */}
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.4, type: 'spring' }}
                  className="text-5xl font-extrabold my-4 text-blue-300"
                >
                  {data.amount.toFixed(4)}
                  <span className="text-xl ml-2 text-white/60">TON</span>
                </motion.div>

                {/* Payment Method */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.5 }}
                  className="bg-white/10 rounded-xl px-4 py-3 mb-3"
                >
                  <div className="flex items-center justify-center gap-2">
                    <Wallet className="w-4 h-4 text-white/60" />
                    <p className="text-sm text-white/60">Payment Method</p>
                  </div>
                  <p className="text-white font-medium">{data.paymentMethod}</p>
                </motion.div>

                {/* Transaction Reference */}
                {data.transactionRef && (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.55 }}
                    className="bg-white/5 rounded-xl px-4 py-3 mb-4"
                  >
                    <div className="flex items-center justify-center gap-2">
                      <ExternalLink className="w-4 h-4 text-white/60" />
                      <p className="text-sm text-white/60">Reference</p>
                    </div>
                    <p className="text-white/80 font-mono text-sm break-all">{data.transactionRef}</p>
                  </motion.div>
                )}

                {/* Action Button */}
                <motion.button
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                  onClick={handleClose}
                  className="w-full py-4 rounded-2xl font-bold text-white transition-all bg-gradient-to-r from-blue-500 to-purple-500 hover:from-blue-400 hover:to-purple-400 shadow-lg shadow-blue-500/30"
                >
                  Awesome! 💎
                </motion.button>
              </div>

              {/* Decorative Background Elements */}
              <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
                <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full blur-3xl bg-blue-400/20" />
                <div className="absolute -bottom-20 -left-20 w-40 h-40 rounded-full blur-3xl bg-purple-400/20" />
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
